/**
 * SUSA Global Checkout — Stripe Adaptive Pricing
 *
 * Two responsibilities:
 * 1. GEO PRICE DISPLAY — Detects visitor country via Shopify localization,
 *    fetches exchange rates, converts TTD prices to local currency for display.
 *    T&T visitors see TTD only. All others see local currency + TTD below.
 *
 * 2. CHECKOUT INTERCEPT — Intercepts Shopify's "Proceed to Checkout" button,
 *    reads the current cart, sends to Railway backend, redirects to Stripe
 *    Checkout where customer is actually charged in their local currency.
 */

(function () {
  'use strict';

  // ─────────────────────────────────────────────────────────────────────────────
  // Config
  // ─────────────────────────────────────────────────────────────────────────────

  const BACKEND_URL = 'https://susa-checkout-worker.ayomarcelle.workers.dev';
  const EXCHANGE_RATE_API = 'https://open.er-api.com/v6/latest/TTD'; // Free, no key needed
  const CACHE_KEY = 'susa_fx_rates';
  const CACHE_TTL = 6 * 60 * 60 * 1000; // 6 hours

  // Country code → currency code mapping
  // T&T stays TTD, others get their local currency
  const COUNTRY_CURRENCY = {
    TT: 'TTD', // Trinidad & Tobago — no conversion
    US: 'USD', CA: 'CAD', GB: 'GBP', AU: 'AUD', NZ: 'NZD',
    ZA: 'ZAR', NG: 'NGN', GH: 'GHS', KE: 'KES', UG: 'UGX',
    DE: 'EUR', FR: 'EUR', IT: 'EUR', ES: 'EUR', PT: 'EUR', NL: 'EUR',
    BE: 'EUR', AT: 'EUR', IE: 'EUR', FI: 'EUR', GR: 'EUR',
    JP: 'JPY', SG: 'SGD', HK: 'HKD', IN: 'INR', MY: 'MYR',
    AE: 'AED', SA: 'SAR', QA: 'QAR',
    BR: 'BRL', MX: 'MXN', AR: 'ARS', CO: 'COP',
    BB: 'BBD', JM: 'JMD',
  };

  // Currency display symbols
  const CURRENCY_SYMBOLS = {
    TTD: 'TT$', USD: '$', CAD: 'CA$', GBP: '£', AUD: 'A$', NZD: 'NZ$',
    ZAR: 'R', NGN: '₦', GHS: 'GH₵', KES: 'KSh', UGX: 'USh',
    EUR: '€', JPY: '¥', SGD: 'S$', HKD: 'HK$', INR: '₹', MYR: 'RM',
    AED: 'AED', SAR: 'SAR', QAR: 'QAR', BRL: 'R$', MXN: 'MX$',
    ARS: 'ARS', COP: 'COP', BBD: 'Bds$', JMD: 'J$',
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // State
  // ─────────────────────────────────────────────────────────────────────────────

  let visitorCountry = 'TT';   // Default T&T until detected
  let visitorCurrency = 'TTD';
  let fxRates = {};            // TTD → other currencies

  // ─────────────────────────────────────────────────────────────────────────────
  // 1. Detect visitor country from Shopify localization (passed via SUSA_CONFIG)
  // ─────────────────────────────────────────────────────────────────────────────

  async function detectCountry() {
    // Primary: Cloudflare Worker /geo — uses Cloudflare's Anycast network (most accurate)
    try {
      const res = await fetch(`${BACKEND_URL}/geo`, { cache: 'no-store' });
      if (res.ok) {
        const data = await res.json();
        if (data.country && data.country.length === 2) {
          visitorCountry = data.country;
          visitorCurrency = COUNTRY_CURRENCY[data.country] || 'USD';
          return visitorCurrency;
        }
      }
    } catch (e) {
      // Fall through to ipapi.co
    }
    // Fallback: ipapi.co
    try {
      const res = await fetch('https://ipapi.co/country/', { cache: 'no-store' });
      if (res.ok) {
        const country = (await res.text()).trim();
        if (country && country.length === 2) {
          visitorCountry = country;
          visitorCurrency = COUNTRY_CURRENCY[country] || 'USD';
          return visitorCurrency;
        }
      }
    } catch (e) {
      // Fall through to Shopify fallback
    }
    // Last resort: Shopify localization
    const country = (window.SUSA_CONFIG && window.SUSA_CONFIG.visitorCountry) || 'TT';
    visitorCountry = country;
    visitorCurrency = COUNTRY_CURRENCY[country] || 'USD';
    return visitorCurrency;
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. Fetch exchange rates (cached 6h in localStorage)
  // ─────────────────────────────────────────────────────────────────────────────

  async function fetchRates() {
    if (visitorCurrency === 'TTD') return; // No conversion needed for T&T

    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        const { rates, timestamp } = JSON.parse(cached);
        if (Date.now() - timestamp < CACHE_TTL) {
          fxRates = rates;
          return;
        }
      }

      const res = await fetch(EXCHANGE_RATE_API);
      const data = await res.json();
      if (data.result === 'success') {
        fxRates = data.rates;
        localStorage.setItem(CACHE_KEY, JSON.stringify({ rates: fxRates, timestamp: Date.now() }));
      }
    } catch (err) {
      console.warn('[SUSA] Could not fetch exchange rates:', err);
    }
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. Convert TTD amount (in cents) to visitor currency for display
  // ─────────────────────────────────────────────────────────────────────────────

  function convertPrice(ttdCents) {
    if (visitorCurrency === 'TTD') return null; // No conversion
    const rate = fxRates[visitorCurrency];
    if (!rate) return null;

    const ttdAmount = ttdCents / 100;
    const converted = ttdAmount * rate;
    const symbol = CURRENCY_SYMBOLS[visitorCurrency] || visitorCurrency + ' ';

    // Format: no decimals for JPY/KES/UGX, 2 decimals for everything else
    const noDecimals = ['JPY', 'KES', 'UGX', 'NGN'];
    const formatted = noDecimals.includes(visitorCurrency)
      ? Math.round(converted).toLocaleString()
      : converted.toFixed(2);

    return `${symbol}${formatted}`;
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. Inject converted prices alongside TTD prices on the page
  // ─────────────────────────────────────────────────────────────────────────────

  function injectConvertedPrices() {
    if (visitorCurrency === 'TTD') return; // T&T sees TTD only

    // Replace sale/regular prices with local currency
    document.querySelectorAll('[data-price-ttd-cents]:not([data-currency-converted])').forEach(el => {
      const cents = parseInt(el.getAttribute('data-price-ttd-cents'), 10);
      if (!cents) return;
      const converted = convertPrice(cents);
      if (!converted) return;
      el.textContent = converted;
      el.setAttribute('data-currency-converted', visitorCurrency);
    });

    // Replace compare-at (strikethrough) prices with local currency
    document.querySelectorAll('[data-compare-ttd-cents]:not([data-currency-converted])').forEach(el => {
      const cents = parseInt(el.getAttribute('data-compare-ttd-cents'), 10);
      if (!cents) return;
      const converted = convertPrice(cents);
      if (!converted) return;
      el.textContent = converted;
      el.setAttribute('data-currency-converted', visitorCurrency);
    });
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // 4b. Convert cart prices by scanning for TTD-formatted text (no data attrs needed)
  // Handles cached cart.js versions and VIP/sale items that have no data-price-ttd-cents
  // ─────────────────────────────────────────────────────────────────────────────

  function convertCartByText() {
    if (visitorCurrency === 'TTD') return;

    var cart = document.getElementById('cart-drawer') || document.getElementById('cart-drawer-content');
    if (!cart) return;

    // Match: $1,234.56 TTD or $1.00 TTD or TT$1.00
    var ttdPattern = /(?:TT\$|\$)([\d,]+\.?\d*)\s*TTD?/g;

    function walkAndConvert(node) {
      if (node.nodeType === 3) { // text node
        var text = node.textContent;
        if (!ttdPattern.test(text)) return;
        ttdPattern.lastIndex = 0;

        var newText = text.replace(ttdPattern, function(match, amount) {
          var ttdAmount = parseFloat(amount.replace(/,/g, ''));
          if (isNaN(ttdAmount)) return match;
          var cents = Math.round(ttdAmount * 100);
          var converted = convertPrice(cents);
          return converted || match;
        });

        if (newText !== text) node.textContent = newText;
        return;
      }

      // Skip script/style nodes and already-converted elements
      if (node.nodeType === 1) {
        if (node.tagName === 'SCRIPT' || node.tagName === 'STYLE') return;
        if (node.getAttribute('data-currency-converted')) return;
        node.childNodes.forEach(walkAndConvert);
        if (node.textContent && ttdPattern.test(node.textContent)) {
          node.setAttribute('data-currency-converted', visitorCurrency);
        }
        ttdPattern.lastIndex = 0;
      }
    }

    cart.childNodes.forEach(walkAndConvert);
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. Intercept Shopify checkout button — redirect to Stripe instead
  // ─────────────────────────────────────────────────────────────────────────────

  async function initCheckoutIntercept() {
    // Listen for clicks on checkout buttons sitewide (cart drawer + cart page)
    document.addEventListener('click', async function (e) {
      const btn = e.target.closest('[name="checkout"], .cart__checkout-button, .cart-checkout-btn, [data-susa-checkout]');
      if (!btn) return;

      // T&T visitors use normal Shopify checkout
      if (visitorCurrency === 'TTD') return;

      e.preventDefault();
      e.stopPropagation();

      await redirectToStripeCheckout(btn);
    }, true);
  }

  async function redirectToStripeCheckout(btn) {
    const originalText = btn.textContent;
    btn.textContent = 'Redirecting…';
    btn.disabled = true;

    try {
      // Fetch current cart from Shopify
      const cartRes = await fetch('/cart.js');
      const cart = await cartRes.json();

      if (!cart.items || cart.items.length === 0) {
        alert('Your cart is empty.');
        btn.textContent = originalText;
        btn.disabled = false;
        return;
      }

      // Convert TTD prices to visitor's local currency
      const rate = fxRates[visitorCurrency];
      const items = cart.items.map(item => ({
        variant_id: item.variant_id,
        product_id: item.product_id,
        title: item.product_title,
        variant_title: item.variant_title,
        price: item.price,          // Original TTD cents (for Shopify order record)
        converted_price: rate ? Math.round(item.price * rate) : item.price, // Local currency cents
        quantity: item.quantity,
        image: item.image ? 'https:' + item.image : null,
      }));

      const payload = {
        items,
        currency: visitorCurrency,  // Tell Worker what currency to charge in
        successUrl: `${window.location.origin}/pages/order-confirmed`,
        cancelUrl: window.location.href,
        customerEmail: window.SUSA_CONFIG?.pinkVIP?.customer?.email || undefined,
        note: cart.note || '',
      };

      // Call Railway backend to create Stripe Checkout Session
      const res = await fetch(`${BACKEND_URL}/session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (!res.ok || !data.url) {
        throw new Error(data.error || 'Failed to create checkout session');
      }

      // Redirect to Stripe Checkout
      window.location.href = data.url;

    } catch (err) {
      console.error('[SUSA] Stripe checkout error:', err);
      btn.textContent = originalText;
      btn.disabled = false;
      alert('Could not start checkout. Please try again.');
    }
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. Currency badge CSS injection
  // ─────────────────────────────────────────────────────────────────────────────

  function injectStyles() {
    // No additional styles needed — prices are fully replaced in local currency
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Init
  // ─────────────────────────────────────────────────────────────────────────────

  async function init() {
    await detectCountry();
    injectStyles();
    await fetchRates();
    injectConvertedPrices();
    initCheckoutIntercept();

    // Re-run price injection when cart drawer updates
    document.addEventListener('susa:cart:updated', function() {
      injectConvertedPrices();
      convertCartByText();
    });
    document.addEventListener('susa:cart:open', function() {
      injectConvertedPrices();
      convertCartByText();
    });
    document.addEventListener('cart:updated', function() {
      setTimeout(function() { injectConvertedPrices(); convertCartByText(); }, 100);
    });

    // MutationObserver: catch cart renders even without events
    var cartDrawer = document.getElementById('cart-drawer') || document.getElementById('cart-drawer-content');
    if (cartDrawer) {
      var observer = new MutationObserver(function() {
        injectConvertedPrices();
        convertCartByText();
      });
      observer.observe(cartDrawer, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose for debugging
  window.SusaGlobalCheckout = { detectCountry, convertPrice, fxRates: () => fxRates, currency: () => visitorCurrency };

})();
